import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Study } from 'pages/StudyList/StudyList';

export type NotesFormParams = {
  note: string;
  category: string;
};

export const useNotesQueries = (args: {
  enabled?: boolean;
  reviewStudyId: Study['id'];
  intialCount: number;
  fetchNotes: (reviewStudyId: Study['id']) => Promise<any>;
  createNote: (
    note: string,
    category: string,
    reviewStudyId: Study['id']
  ) => Promise<any>;
}) => {
  const { reviewStudyId, createNote, intialCount, fetchNotes, enabled } = args;

  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    return queryClient.invalidateQueries({
      queryKey: ['notes', reviewStudyId],
    });
  };

  const { isFetching, data: notes, error, status } = useQuery({
    enabled,
    queryKey: ['notes', reviewStudyId],
    queryFn: () => fetchNotes(reviewStudyId),
    staleTime: 60_000,
  });

  const [notesCount, setNotesCount] = useState(intialCount);

  const { mutate, isLoading: isCreating, error: mutationError } = useMutation({
    mutationFn: ({ note, category }: NotesFormParams) =>
      createNote(note, category, reviewStudyId),
    onSuccess: invalidateQueries,
  });

  // Update the count when the query updates, things will stay in sync somewhat
  useEffect(() => {
    if (status === 'success') {
      setNotesCount(notes.length);
    }
  }, [notes?.length, status]);

  return {
    isFetching,
    notes,
    error,
    mutate,
    isCreating,
    mutationError,
    notesCount,
    invalidateQueries,
  };
};
